import React from 'react';
import './FeaturesSection.css';

const FeaturesSection = () => {
  const features = [
    {
      icon: '📊',
      title: 'CSV Import',
      description: 'Import thousands of survey points from CSV files with intelligent data parsing',
      type: 'import'
    },
    {
      icon: '🔄',
      title: 'KML → CAD',
      description: 'Convert Google Earth KML files to native CAD layers seamlessly',
      type: 'kml-cad'
    },
    {
      icon: '🏔️',
      title: 'Contour Creation',
      description: 'Generate major and minor contours from point datasets instantly',
      type: 'contour'
    },
    {
      icon: '🏷️',
      title: 'Contour Annotation',
      description: '6 annotation types for professional contour labeling',
      type: 'annotation'
    },
    {
      icon: '📦',
      title: 'Blockpost',
      description: 'Efficient block posting and management for survey data',
      type: 'blockpost'
    },
    {
      icon: '📋',
      title: 'Coordinate Table',
      description: 'Auto-generate coordinate tables with precise formatting',
      type: 'coordinate'
    },
    {
      icon: '⛰️',
      title: 'Earthwork Calculation',
      description: 'Calculate cut and fill volumes between surfaces accurately',
      type: 'earthwork'
    },
    {
      icon: '📐',
      title: 'CS Drawing',
      description: 'Professional cross-section drawings with automated layout',
      type: 'cross-section'
    },
    {
      icon: '📈',
      title: 'Volume Calculations',
      description: 'Longitudinal section and volume calculations for projects',
      type: 'volume'
    },
    {
      icon: '📤',
      title: 'Excel Export',
      description: 'Export survey data and calculations to Excel with one click',
      type: 'export'
    },
    {
      icon: '🔺',
      title: 'TIN Surface',
      description: 'Create TIN surfaces and perform surface-related processing',
      type: 'tin'
    }
  ];

  return (
    <section className="features-section" id="features">
      <div className="section-header">
        <h2 className="section-title">Complete Survey Toolkit</h2>
        <p className="section-subtitle">Everything you need for professional surveying in one plugin</p>
      </div>
      
      <div className="features-grid">
        {features.map((feature, index) => (
          <div key={index} className="feature-card">
            <div className="feature-visual">
              <FeatureAnimation type={feature.type} />
            </div>
            <h3 className="feature-title">{feature.title}</h3>
            <p className="feature-description">{feature.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

// SVG Animation Component
const FeatureAnimation = ({ type }) => {
  switch(type) {
    case 'import':
      return (
        <svg viewBox="0 0 100 100" className="anim-svg">
          <rect x="20" y="30" width="60" height="40" rx="5" fill="none" stroke="#10B981" strokeWidth="2"/>
          <path d="M35 50 L50 65 L65 50" fill="none" stroke="#10B981" strokeWidth="2" className="draw-line"/>
          <line x1="50" y1="65" x2="50" y2="40" stroke="#10B981" strokeWidth="2" className="draw-line"/>
          <circle cx="50" cy="20" r="3" fill="#10B981" className="blink"/>
        </svg>
      );
    
    case 'kml-cad':
      return (
        <svg viewBox="0 0 100 100" className="anim-svg">
          <path d="M30 70 L50 30 L70 70 Z" fill="none" stroke="#10B981" strokeWidth="2" className="morph-shape"/>
          <circle cx="50" cy="40" r="5" fill="#10B981" className="pulse-circle"/>
          <rect x="25" y="70" width="50" height="5" rx="2" fill="#10B981" className="grow-width"/>
        </svg>
      );
    
    case 'contour':
      return (
        <svg viewBox="0 0 100 100" className="anim-svg">
          {[0, 1, 2, 3].map((i) => (
            <path
              key={i}
              d={`M ${20 + i*5} ${80 - i*15} Q ${40} ${50 - i*10} ${60 - i*5} ${40 - i*10} T ${80 - i*5} ${20 + i*5}`}
              fill="none"
              stroke="#10B981"
              strokeWidth="1.5"
              className="contour-line"
              style={{ animationDelay: `${i * 0.5}s` }}
            />
          ))}
        </svg>
      );
    
    case 'annotation':
      return (
        <svg viewBox="0 0 100 100" className="anim-svg">
          <line x1="30" y1="30" x2="70" y2="30" stroke="#10B981" strokeWidth="2" className="draw-line"/>
          <text x="25" y="20" fontSize="8" fill="#10B981" className="fade-in">Label</text>
          <circle cx="50" cy="30" r="3" fill="#10B981" className="blink"/>
        </svg>
      );
    
    case 'blockpost':
      return (
        <svg viewBox="0 0 100 100" className="anim-svg">
          {[0, 1, 2].map((i) => (
            <rect
              key={i}
              x={20 + i*25}
              y="30"
              width="15"
              height="15"
              fill="none"
              stroke="#10B981"
              strokeWidth="2"
              className="stack-block"
              style={{ animationDelay: `${i * 0.3}s` }}
            />
          ))}
        </svg>
      );
    
    case 'coordinate':
      return (
        <svg viewBox="0 0 100 100" className="anim-svg">
          <line x1="20" y1="30" x2="80" y2="30" stroke="#10B981" strokeWidth="1" className="draw-line"/>
          <line x1="20" y1="50" x2="80" y2="50" stroke="#10B981" strokeWidth="1" className="draw-line" style={{ animationDelay: '0.3s' }}/>
          <line x1="20" y1="70" x2="80" y2="70" stroke="#10B981" strokeWidth="1" className="draw-line" style={{ animationDelay: '0.6s' }}/>
          <circle cx="30" cy="30" r="2" fill="#10B981" className="blink"/>
          <circle cx="50" cy="50" r="2" fill="#10B981" className="blink" style={{ animationDelay: '0.5s' }}/>
        </svg>
      );
    
    case 'earthwork':
      return (
        <svg viewBox="0 0 100 100" className="anim-svg">
          <path d="M20 70 L35 50 L50 65 L65 40 L80 60" fill="none" stroke="#10B981" strokeWidth="2" className="draw-line"/>
          <path d="M20 70 L35 50 L50 65 L65 40 L80 60 L80 70 Z" fill="rgba(16,185,129,0.2)" className="fill-area"/>
        </svg>
      );
    
    case 'cross-section':
      return (
        <svg viewBox="0 0 100 100" className="anim-svg">
          <line x1="50" y1="10" x2="50" y2="90" stroke="#10B981" strokeWidth="1" className="draw-line"/>
          <line x1="20" y1="50" x2="80" y2="50" stroke="#10B981" strokeWidth="1" className="draw-line" style={{ animationDelay: '0.3s' }}/>
          <path d="M30 50 L50 35 L70 50" fill="none" stroke="#10B981" strokeWidth="2" className="draw-line" style={{ animationDelay: '0.6s' }}/>
        </svg>
      );
    
    case 'volume':
      return (
        <svg viewBox="0 0 100 100" className="anim-svg">
          <rect x="30" y="40" width="40" height="40" fill="none" stroke="#10B981" strokeWidth="2" className="grow-height"/>
          <text x="42" y="65" fontSize="12" fill="#10B981" className="fade-in">V</text>
        </svg>
      );
    
    case 'export':
      return (
        <svg viewBox="0 0 100 100" className="anim-svg">
          <path d="M50 80 L50 20" stroke="#10B981" strokeWidth="2" className="draw-line"/>
          <path d="M40 30 L50 20 L60 30" fill="none" stroke="#10B981" strokeWidth="2" className="draw-line" style={{ animationDelay: '0.2s' }}/>
          <rect x="30" y="75" width="40" height="10" fill="none" stroke="#10B981" strokeWidth="2" className="draw-line" style={{ animationDelay: '0.4s' }}/>
        </svg>
      );
    
    case 'tin':
      return (
        <svg viewBox="0 0 100 100" className="anim-svg">
          <circle cx="50" cy="50" r="5" fill="#10B981" className="pulse-circle"/>
          <circle cx="30" cy="30" r="3" fill="#10B981" className="pulse-circle" style={{ animationDelay: '0.3s' }}/>
          <circle cx="70" cy="70" r="3" fill="#10B981" className="pulse-circle" style={{ animationDelay: '0.6s' }}/>
          <line x1="50" y1="50" x2="30" y2="30" stroke="#10B981" strokeWidth="1" className="draw-line"/>
          <line x1="50" y1="50" x2="70" y2="70" stroke="#10B981" strokeWidth="1" className="draw-line" style={{ animationDelay: '0.3s' }}/>
        </svg>
      );
    
    default:
      return null;
  }
};

export default FeaturesSection;